import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   OperationalSummaryLine
   ───────────────────────────────────────────────────────────────────
   Phase 2 of the Mission Control Evolution spec.

   Renders a single calm sentence describing the current operational
   state of the deal. Fetched from /api/deals/{id}/intel-rail
   (`intel.operational_summary`). Deterministic backend composer —
   never AI, never marketing, never alarm-coloured.

   Visual: subtle slate text on a faint translucent strip. Slipping +
   chain pressure get a soft amber/rose accent dot; everything else
   stays slate. No icons, no badges — premium typographic restraint.
   ─────────────────────────────────────────────────────────────────── */

const TONE_DOT = {
  calm:     "#10B981",   // green-soft
  watching: "#94A3B8",   // slate
  elevated: "#F59E0B",   // amber-soft (never red)
  recovery: "#F43F5E",   // rose-soft, reserved for recovery mode
};

const TONE_BG = {
  calm:     "rgba(16,185,129,0.05)",
  watching: "rgba(148,163,184,0.06)",
  elevated: "rgba(245,158,11,0.06)",
  recovery: "rgba(244,63,94,0.06)",
};

export const OperationalSummaryLine = ({ dealId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/api/deals/${dealId}/intel-rail`);
        if (alive) setSummary(data?.intel?.operational_summary || null);
      } catch {
        if (alive) setSummary(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dealId]);

  if (loading) {
    return (
      <div
        className="mb-4 rounded-xl px-4 py-2.5 cf-pulse"
        style={{ background: "rgba(148,163,184,0.06)" }}
        data-testid="dealroom-operational-summary-loading"
        aria-busy="true"
      >
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
      </div>
    );
  }

  if (!summary?.headline) return null;

  const tone = summary.tone || "watching";
  const dot = TONE_DOT[tone] || TONE_DOT.watching;
  const bg = TONE_BG[tone] || TONE_BG.watching;

  return (
    <div
      className="mb-4 rounded-xl px-4 py-2.5 flex items-start gap-2.5 transition-colors"
      style={{
        background: bg,
        border: "1px solid rgba(148,163,184,0.12)",
        transition: "background 280ms cubic-bezier(0.4,0,0.2,1)",
      }}
      data-testid="dealroom-operational-summary"
    >
      <span
        aria-hidden
        className="inline-block w-1.5 h-1.5 rounded-full mt-2 shrink-0"
        style={{
          background: dot,
          boxShadow: tone !== "calm" ? `0 0 0 3px ${dot}1A` : "none",
        }}
      />
      <div className="min-w-0 flex-1">
        <p
          className="text-[12.5px] font-semibold leading-snug"
          style={{ color: "#0F172A" }}
          data-testid="dealroom-operational-summary-headline"
        >
          {summary.headline}
        </p>
        {summary.evidence?.length ? (
          <p
            className="text-[10.5px] mt-0.5 uppercase tracking-[0.14em] font-bold"
            style={{ color: "#64748B" }}
          >
            {summary.evidence.slice(0, 2).join(" · ")}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default OperationalSummaryLine;
